/** P&L и операции: ответы ручек `/api/pnl`, `/api/bank-operations`, `/api/pnl-rules`, `/api/bank-accounts`. */
import { apiFetch, query } from "@/lib/api";

/** Суммы с сервера — строки (Decimal), чтобы копейки не терялись в пути. */
export type Money = string;

export type ArticleKind = "income" | "expense" | "tax" | "capex" | "transfer" | "owner" | "financing" | "excluded";

export type Article = {
  id: number;
  parent_id: number | null;
  code: string;
  name: string;
  kind: ArticleKind;
  kind_name: string;
  in_pnl: boolean;
};

export type PnlRow = Article & {
  is_group: boolean;
  operations: number;
  /** Строк наличных касс CRM (выплат и оплат) в статье — только при `cash`. */
  kassa_rows: number;
  /** Сколько в строке от реестров эквайера (брутто и комиссия), а не от операций банка. */
  acquirer_adjustment: Money;
  /** Месяц `YYYY-MM` → сумма со знаком (приход +, расход −). */
  values: Record<string, Money>;
  total: Money;
};

export type PnlLine = { values: Record<string, Money>; total: Money };

/** Как ушли деньги: безнал со счёта ИП, перевод физлицу, покупка картой,
 *  снятие наличных с карты, выплата наличной кассы. */
export type Channel = "cashless" | "person" | "card" | "withdraw" | "cash";
/** Каналы операций банка — фильтр списка операций и контрагентов. */
export type BankChannel = Exclude<Channel, "cash">;

export const CHANNEL_LABEL: Record<Channel, string> = {
  cashless: "безнал со счетов ИП",
  person: "переводы физлицам",
  card: "покупки картой",
  withdraw: "снятие наличных с карт",
  cash: "наличные кассы",
};

/** Откуда ушли расходы P&L: счета человека в одном банке одного типа,
 *  наличные касс или удержанное эквайером. */
export type PnlSource = PnlLine & {
  entrepreneur_id: number | null;
  entrepreneur_name: string | null;
  bank_name: string | null;
  kind: "business" | "personal" | "company" | "cash" | "acquirer";
  /** Счета строки — для перехода к операциям. */
  account_ids: number[];
  operations: number;
};

export type Pnl = {
  months: string[];
  rows: PnlRow[];
  /** Сумма — `totals.expense_total`: расходы, налоги и капзатраты P&L. */
  sources: PnlSource[];
  totals: Record<
    | "income"
    /** Неразнесённые расходы — входят в `expense` и в прибыль; неразнесённые приходы — нет. */
    | "unassigned_expense"
    /** Неразнесённые приходы — справочно, в прибыль не идут. */
    | "unassigned_income"
    | "expense"
    | "before_tax"
    | "tax"
    | "net"
    | "capex"
    | "after_capex"
    /** Расходы, налоги и капзатраты — итог `sources`. */
    | "expense_total"
    | "transfer_balance",
    PnlLine
  > & {
    /** `unassigned_expense` по каналам; сумма каналов — строка целиком. */
    unassigned_by_channel: Record<Channel, PnlLine>;
  };
};

/** Несколько ИП — повторы ключа; пусто — все. */
export type EntrepreneurIds = readonly string[];

export type Operation = {
  id: number;
  date: string;
  direction: "in" | "out";
  amount: Money;
  account: string;
  account_kind: "business" | "personal" | "company";
  entrepreneur_name: string;
  counterparty_name: string;
  counterparty_inn: string | null;
  counterparty_account: string | null;
  /** Город контрагента, если его привязали. */
  counterparty_location_name: string | null;
  purpose: string;
  doc_kind: string;
  number: string;
  article_id: number;
  article_name: string;
  article_source: "internal" | "kassa" | "rule" | "manual" | "none";
  rule_id: number | null;
  statement_id: number;
  comments: OperationComment[];
  /** Выплата кассы CRM, которую провела операция; null — не выплата. */
  kassa_expense: OperationPayout | null;
};

/** Выплата CRM, найденная по дню, сумме и имени получателя. */
export type OperationPayout = {
  id: number;
  crm_id: number;
  date: string;
  amount: Money;
  /** ФИО по реквизитам получателя. */
  recipient_name: string | null;
  /** Владелец платья (myUser CRM); null — выплата не владельцу. */
  owner_crm_id: number | null;
  crm_article_name: string | null;
};

/** Комментарий к операции: кто что выяснил, кому поручено разобраться. */
export type OperationComment = { id: number; text: string; user_name: string; date: string };

/** `unassigned` — сколько операций выборки без статьи (на всех страницах). */
export type OperationPage = { total: number; sum: Money; unassigned: number; rows: Operation[] };

export type OperationFilter = {
  /** Одна операция — постоянная ссылка `/pnl/operations?id=…`. */
  id?: number | undefined;
  counterparty_inn?: string | undefined;
  counterparty_name?: string | undefined;
  /** Контрагент не указан: ни ИНН, ни имени. */
  counterparty_blank?: boolean | undefined;
  entrepreneur_id?: EntrepreneurIds | undefined;
  /** Несколько счетов — повторы ключа; пусто — все. */
  account_id?: readonly string[] | undefined;
  /** Только расходы P&L — переход из «Откуда ушли расходы». */
  expense?: boolean | undefined;
  /** Тип денег, как в P&L: `false` — без счетов ИП / без личных счетов. */
  business?: boolean | undefined;
  personal?: boolean | undefined;
  article_id?: number | undefined;
  direction?: "in" | "out" | undefined;
  channel?: BankChannel | undefined;
  search?: string | undefined;
  date_from?: string | undefined;
  date_to?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
};

export type Rule = {
  id: number;
  priority: number;
  direction: "in" | "out" | null;
  counterparty_inn: string | null;
  account_prefix: string | null;
  counterparty_pattern: string | null;
  purpose_pattern: string | null;
  article_id: number;
  article_name: string;
  operations: number;
};

export type RuleNew = {
  article_id: number;
  priority?: number | undefined;
  direction?: "in" | "out" | null | undefined;
  counterparty_inn?: string | undefined;
  account_prefix?: string | undefined;
  counterparty_pattern?: string | undefined;
  purpose_pattern?: string | undefined;
};

export type Account = {
  id: number;
  // null — номер неизвестен: счёт Т-Банка узнан по ИП и продукту (`product`)
  number: string | null;
  product: string | null;
  entrepreneur_id: number;
  entrepreneur_name: string;
  bank_name: string | null;
  kind: "business" | "personal" | "company";
  kind_name: string;
  operations: number;
  date_from: string | null;
  date_to: string | null;
  total_in: Money;
  total_out: Money;
};

export function loadPnl(filter: {
  entrepreneur_id?: EntrepreneurIds | undefined;
  business?: boolean | undefined;
  personal?: boolean | undefined;
  cash?: boolean | undefined;
  date_from?: string | undefined;
  date_to?: string | undefined;
}): Promise<Pnl> {
  return apiFetch<Pnl>(
    `/api/pnl${query({
      entrepreneur_id: filter.entrepreneur_id,
      // `business=false` — без счетов ИП (только личные счета и/или наличные).
      business: filter.business === false ? "false" : undefined,
      // `personal=false` — только счета ИП; по умолчанию сервер берёт все.
      personal: filter.personal === false ? "false" : undefined,
      // Наличные кассы сервер берёт по умолчанию; `cash=false` — только банк.
      cash: filter.cash === false ? "false" : undefined,
      date_from: filter.date_from,
      date_to: filter.date_to,
    })}`,
  );
}

export function loadArticles(): Promise<Article[]> {
  return apiFetch<Article[]>("/api/pnl-articles");
}

export function loadOperations(filter: OperationFilter): Promise<OperationPage> {
  return apiFetch<OperationPage>(
    `/api/bank-operations${query({
      ...filter,
      business: filter.business === false ? "false" : undefined,
      personal: filter.personal === false ? "false" : undefined,
    })}`,
  );
}

export function setOperationArticle(id: number, article_id: number): Promise<Operation> {
  return apiFetch<Operation>(`/api/bank-operations/${id}`, { method: "PATCH", body: JSON.stringify({ article_id }) });
}

export function addOperationComment(id: number, text: string): Promise<Operation> {
  return apiFetch<Operation>(`/api/bank-operations/${id}/comments`, { method: "POST", body: JSON.stringify({ text }) });
}

/** Контрагент — ИНН; без ИНН — имя (`inn: null`). */
export type Counterparty = {
  inn: string | null;
  name: string;
  operations: number;
  total_in: Money;
  total_out: Money;
  date_first: string;
  date_last: string;
  /** Город контрагента (город CRM); `null` — не привязан. */
  location_id: number | null;
  location_name: string | null;
  /** Заметка: кто это, куда разносить; пусто — нет. */
  note: string;
};

export function loadCounterparties(filter: {
  search?: string | undefined;
  entrepreneur_id?: EntrepreneurIds | undefined;
  /** Несколько счетов — повторы ключа; пусто — все. */
  account_id?: readonly string[] | undefined;
  /** Только расходы P&L — переход из «Откуда ушли расходы». */
  expense?: boolean | undefined;
  business?: boolean | undefined;
  personal?: boolean | undefined;
  /** Статья или группа — «Не разнесено» по контрагентам. */
  article_id?: number | undefined;
  direction?: "in" | "out" | undefined;
  channel?: BankChannel | undefined;
  date_from?: string | undefined;
  date_to?: string | undefined;
}): Promise<{ truncated: boolean; rows: Counterparty[] }> {
  return apiFetch(
    `/api/counterparties${query({
      ...filter,
      // Как в P&L: по умолчанию сервер берёт и счета ИП, и личные.
      business: filter.business === false ? "false" : undefined,
      personal: filter.personal === false ? "false" : undefined,
    })}`,
  );
}

/** Адрес операций контрагента: по ИНН, а без ИНН — по имени точно. */
export function counterpartyOperationsUrl(
  cp: { inn: string | null; name: string },
  scope: {
    entrepreneur_id?: EntrepreneurIds | undefined;
    business?: boolean | undefined;
    personal?: boolean | undefined;
    article_id?: number | undefined;
    direction?: "in" | "out" | undefined;
    channel?: BankChannel | undefined;
    date_from?: string | undefined;
    date_to?: string | undefined;
  } = {},
): string {
  return `/pnl/operations${query({
    counterparty_inn: cp.inn ?? undefined,
    counterparty_name: cp.inn ? undefined : cp.name,
    counterparty_blank: !cp.inn && !cp.name,
    entrepreneur_id: scope.entrepreneur_id,
    business: scope.business === false ? "false" : undefined,
    personal: scope.personal === false ? "false" : undefined,
    article_id: scope.article_id,
    direction: scope.direction,
    channel: scope.channel,
    date_from: scope.date_from,
    date_to: scope.date_to,
  })}`;
}

/** Город CRM — к нему привязывают контрагента. */
export type Location = { id: number; name: string; entrepreneur_name: string | null };

export function loadLocations(): Promise<Location[]> {
  return apiFetch<Location[]>("/api/crm-locations");
}

/** Привязать контрагента к городу; `null` — снять привязку. */
export function setCounterpartyLocation(
  cp: { inn: string | null; name: string },
  location_id: number | null,
): Promise<{ location_id: number | null; location_name: string | null }> {
  return apiFetch("/api/counterparty-location", {
    method: "PUT",
    body: JSON.stringify({ inn: cp.inn, name: cp.name, location_id }),
  });
}

/** Заметка о контрагенте; пустой текст — стереть. */
export function setCounterpartyNote(
  cp: { inn: string | null; name: string },
  text: string,
): Promise<{ note: string }> {
  return apiFetch("/api/counterparty-note", {
    method: "PUT",
    body: JSON.stringify({ inn: cp.inn, name: cp.name, text }),
  });
}

// ── Зарплата ────────────────────────────────────────────────────────────────

export type PayrollGroup = "location" | "user" | "role";

export type PayrollRow = {
  /** Салон — id города; сотрудник — CRM-id; роль — её имя. `null` — не указан. */
  key: number | string | null;
  name: string | null;
  role: string | null;
  cash: PnlLine;
  noncash: PnlLine;
  /** Принятые часы CRM по месяцам смены — только разрез «по сотрудникам»; `null` — время не ведётся. */
  hours: PnlLine | null;
  payouts: number;
};

export type Payroll = {
  months: string[];
  group: PayrollGroup;
  /** CRM не ответила — часов нет, деньги есть. */
  hours_failed: boolean;
  summary: {
    cash: PnlLine & { count: number };
    bank: PnlLine & { count: number };
    /** Строка P&L «ФОТ: зарплата» (плюсом). */
    pnl: PnlLine;
    /** Безнал касс CRM — справочно, в P&L не входит. */
    crm_noncash: PnlLine;
    crm_noncash_by_kassa: (PnlLine & { kassa_name: string })[];
  };
  rows: PayrollRow[];
};

export function loadPayroll(filter: {
  group: PayrollGroup;
  entrepreneur_id?: EntrepreneurIds | undefined;
  business?: boolean | undefined;
  personal?: boolean | undefined;
  cash?: boolean | undefined;
  location_id?: number | undefined;
  date_from?: string | undefined;
  date_to?: string | undefined;
  /** Один сотрудник — история в панели. */
  user_crm_id?: number | undefined;
}): Promise<Payroll> {
  const off = (flag: boolean | undefined) => (flag === false ? "false" : undefined);
  return apiFetch<Payroll>(
    `/api/payroll${query({
      ...filter,
      business: off(filter.business),
      personal: off(filter.personal),
      cash: off(filter.cash),
    })}`,
  );
}

// ── Банковское обслуживание ────────────────────────────────────────────────

type Priced = PnlLine & { count: number; price_min: Money | null; price_max: Money | null };

export type BankFeeAccount = Priced & {
  account_id: number;
  entrepreneur: string;
  bank: string;
  /** `*1234` — хвост номера, или продукт банка. */
  account: string;
};

export type BankFeeKind = Priced & {
  code: string;
  name: string;
  /** За что цена: «штука», «месяц», «карта». */
  unit: string;
  accounts: BankFeeAccount[];
};

export type BankFeeOperation = {
  id: number;
  date: string;
  amount: Money;
  purpose: string;
  kind: string;
  account_id: number;
  entrepreneur: string;
  bank: string;
  account: string;
};

export type BankService = {
  months: string[];
  article_id: number;
  /** Строка P&L «Банковское обслуживание» (плюсом). */
  total: PnlLine & { count: number };
  kinds: BankFeeKind[];
  operations: BankFeeOperation[];
  /** «Включена комиссия N руб.» в любых операциях — справочно. */
  embedded: PnlLine & {
    count: number;
    turnover: Money;
    by_article: { article: string; count: number; fee: Money; turnover: Money }[];
  };
};

export function loadBankService(filter: {
  entrepreneur_id?: EntrepreneurIds | undefined;
  business?: boolean | undefined;
  personal?: boolean | undefined;
  cash?: boolean | undefined;
  date_from?: string | undefined;
  date_to?: string | undefined;
}): Promise<BankService> {
  const off = (flag: boolean | undefined) => (flag === false ? "false" : undefined);
  return apiFetch<BankService>(
    `/api/bank-service${query({
      ...filter,
      business: off(filter.business),
      personal: off(filter.personal),
      cash: off(filter.cash),
    })}`,
  );
}

export function reclassify(): Promise<{ changed: number }> {
  return apiFetch("/api/bank-operations/reclassify", { method: "POST" });
}

export function loadRules(): Promise<Rule[]> {
  return apiFetch<Rule[]>("/api/pnl-rules");
}

export function createRule(rule: RuleNew): Promise<{ id: number; changed: number }> {
  return apiFetch("/api/pnl-rules", { method: "POST", body: JSON.stringify(rule) });
}

export function archiveRule(id: number): Promise<{ id: number; changed: number }> {
  return apiFetch(`/api/pnl-rules/${id}`, { method: "DELETE" });
}

export function loadAccounts(): Promise<Account[]> {
  return apiFetch<Account[]>("/api/bank-accounts");
}

export function parseAllStatements(): Promise<{ checked: number; parsed: number }> {
  return apiFetch("/api/bank-statements/parse", { method: "POST" });
}

const MONEY = new Intl.NumberFormat("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MONEY_ROUND = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 });

/** Сумма для глаз. `round` — без копеек (матрица P&L). */
export function money(value: Money | number | undefined, round = false): string {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  if (!n) return round ? "—" : "0,00";
  return (round ? MONEY_ROUND : MONEY).format(n);
}

const MONTHS = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];

/** `2026-03` → `мар 26`. */
export function monthLabel(month: string): string {
  const [year, mon] = month.split("-");
  return `${MONTHS[Number(mon) - 1] ?? mon} ${year?.slice(2)}`;
}

/** Выбор статьи: только статьи внутри групп (и «Не разнесено»), по группам. */
export function articleOptions(articles: Article[]): { group: string; items: Article[] }[] {
  const groups = articles.filter((a) => a.parent_id === null);
  return groups.map((g) => ({
    group: g.name,
    items: g.code === "unassigned" ? [g] : articles.filter((a) => a.parent_id === g.id),
  }));
}
